import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { getFavoriteRecipes } from '../utils/recipeService';
import { useMember } from '../contexts/MemberContext';
import { getProgramCompletions, getUserGoals, getWelcomeModalStatus, markWelcomeModalAsSeen, getProgressEntries, getWeightSettings } from '../utils/syncService';
import { getCompletedWorkouts } from '../utils/workoutService';
import NotificationBell from '../components/NotificationBell';
import PushNotificationPrompt from '../components/PushNotificationPrompt';

const Dashboard = () => {
  const navigate = useNavigate();
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [favIndex, setFavIndex] = useState(0);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const { logout } = useMember();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Load favorites
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    let userEmail = null;
    try {
      const { data, error } = await supabase.auth.getUser();
      if (!error) userEmail = data?.user?.email || null;
    } catch (e) {
      // ignore
    }
    if (!userEmail) {
      try {
        userEmail = localStorage.getItem('userEmail');
      } catch (e) {
        userEmail = null;
      }
    }
    const allFavorites = [];

    try {
      // Load favorite recipes from Supabase
      if (userEmail) {
        const result = await getFavoriteRecipes(userEmail);
        if (result.success && result.data.length > 0) {
          const recipeFavs = result.data.slice(0, 3).map(recipe => ({
            title: recipe.title,
            subtitle: recipe.meal_type || 'Recipe',
            to: '/recipes',
            icon: recipe.image_url ? '📸' : '🍽️',
            color: 'bg-sage-50'
          }));
          allFavorites.push(...recipeFavs);
        }
      }

      // Load saved resources from localStorage
      try {
        const savedResourceIds = JSON.parse(localStorage.getItem('savedResources') || '[]');
        if (savedResourceIds.length > 0) {
          // Fetch resource details from Supabase
          const { data: resources } = await supabase
            .from('resources')
            .select('id, title, type, category')
            .in('id', savedResourceIds)
            .limit(3);
          
          if (resources && resources.length > 0) {
            const resourceFavs = resources.map(resource => ({
              title: resource.title,
              subtitle: resource.type || 'Article',
              to: `/resources/${resource.id}`,
              icon: '📚',
              color: 'bg-purple-50'
            }));
            allFavorites.push(...resourceFavs);
          }
        }
      } catch (e) {
        console.error('Error loading saved resources:', e);
      }

      // Set combined favorites (limit to 5 total)
      setFavoriteItems(allFavorites.slice(0, 5));
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };


  // One-time welcome modal shown on first member login after subscribing
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  // PWA install prompt
  const [showPwaPrompt, setShowPwaPrompt] = useState(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const dismissed = localStorage.getItem('pwaPromptDismissed') === 'true';
    return !isStandalone && !dismissed;
  });
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const dismissPwaPrompt = () => {
    setShowPwaPrompt(false);
    try { localStorage.setItem('pwaPromptDismissed', 'true'); } catch (e) {}
  };
  // Floating Help modal
  const [showSupportModal, setShowSupportModal] = useState(false);
  // Today's Focus rotation (9-day cycle from first visit)
  const focusSuggestions = [
    { title: 'Try a new recipe', subtitle: 'Browse something fresh.', to: '/recipes', cta: 'Browse recipes' },
    { title: 'Share a small win', subtitle: 'Celebrate a tiny victory.', to: '/forum?tab=success-stories', cta: 'Share now' },
    { title: 'Earn a workout streak', subtitle: 'Complete a quick session.', to: '/workouts', cta: 'Start workout' },
    { title: 'Complete your habits', subtitle: 'One small step today.', to: '/my-goals', cta: 'Check habits' },
    { title: 'Revisit your goals', subtitle: 'Refocus your targets.', to: '/my-goals', cta: 'Review goals' },
    { title: 'Stretch for 5 minutes', subtitle: 'Loosen up gently.', to: '/workouts', cta: 'Start stretch' },
    { title: 'Write a journal reflection', subtitle: 'A quick check-in with yourself.', to: '/journal', cta: 'Open journal' },
    { title: 'Update your progress photos', subtitle: 'It might be time for a new check-in.', to: '/my-progress', cta: 'Add photos' },
    { title: 'Log cycle symptoms', subtitle: 'Track patterns to understand your body.', to: '/cycle-tracker', cta: 'Log symptoms' },
    { title: 'Log your recent period', subtitle: 'Keep your cycle history up to date.', to: '/cycle-tracker', cta: 'Log period' }
  ];
  const [focusIndex, setFocusIndex] = useState(0);

  // Initialize avatar and name from localStorage cache for instant display, then Supabase updates in background
  const [avatarUrl, setAvatarUrl] = useState(() => {
    try {
      return localStorage.getItem('memberAvatarUrl') || null;
    } catch {
      return null;
    }
  });
  const [profileName, setProfileName] = useState(() => {
    try {
      return localStorage.getItem('memberProfileName') || 'Member';
    } catch {
      return 'Member';
    }
  });
  const [loadingProfile, setLoadingProfile] = useState(false); // Changed to false for instant display
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Real stats from Supabase
  const [workoutsCompleted, setWorkoutsCompleted] = useState(0);
  const [goalProgress, setGoalProgress] = useState(0);
  const [workoutStreak, setWorkoutStreak] = useState(0);
  
  // Featured resource for Wellness Hub
  const [featuredResource, setFeaturedResource] = useState(null);
  const [loadingResource, setLoadingResource] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Load user profile name, avatar, and admin status from Supabase (background update)
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Ensure userEmail is always stored for data sync
          if (user.email && !localStorage.getItem('userEmail')) {
            localStorage.setItem('userEmail', user.email);
            console.log('📊 Dashboard: Recovered userEmail from auth:', user.email);
          }
          
          // Try to get name, avatar, and admin status from members table
          const { data: member, error } = await supabase
            .from('members')
            .select('first_name, last_name, is_admin, avatar_url')
            .eq('auth_id', user.id)
            .single();
          
          if (member && member.first_name) {
            const fullName = `${member.first_name} ${member.last_name || ''}`.trim();
            setProfileName(fullName);
            setIsAdmin(member.is_admin || false);
            
            // Load avatar from Supabase
            if (member.avatar_url) {
              setAvatarUrl(member.avatar_url);
              // Cache for instant display on next load
              localStorage.setItem('memberAvatarUrl', member.avatar_url);
            }
            
            // Cache name for next time
            localStorage.setItem('memberProfileName', fullName);
          } else {
            setProfileName('Member');
          }
        }
      } catch (error) {
        console.log('Could not load profile name:', error);
      }
    };
    
    loadUserProfile();
  }, []);

  // Load real stats from Supabase
  useEffect(() => {
    const loadStats = async () => {
      let userEmail = localStorage.getItem('userEmail');
      
      // If email not in localStorage, try to recover from Supabase auth session
      if (!userEmail) {
        try {
          const { data } = await supabase.auth.getUser();
          if (data?.user?.email) {
            userEmail = data.user.email;
            localStorage.setItem('userEmail', userEmail);
            console.log('📊 Dashboard: Recovered email from auth for stats:', userEmail);
          }
        } catch (e) { /* ignore */ }
      }

      let totalCompleted = 0;
      let allDates = [];

      // Always check localStorage first (works regardless of Supabase)
      try {
        const localCompleted = JSON.parse(localStorage.getItem('completedWorkouts') || '{}');
        // Count only timestamp-based keys (contain _) to avoid double-counting
        // Older entries without _ still count as 1 each
        const allKeys = Object.keys(localCompleted);
        const timestampKeys = allKeys.filter(k => /_\d{13}$/.test(k));
        const plainKeys = allKeys.filter(k => !/_\d{13}$/.test(k));
        // If there are timestamp keys, count those (each is a unique completion)
        // Plain keys that also have a matching timestamp key are duplicates — skip them
        const timestampPrefixes = new Set(timestampKeys.map(k => k.replace(/_\d{13}$/, '')));
        const uniquePlainKeys = plainKeys.filter(k => !timestampPrefixes.has(k));
        const localCount = timestampKeys.length + uniquePlainKeys.length;
        console.log('📊 Dashboard: localStorage completedWorkouts count:', localCount, '(timestamp:', timestampKeys.length, 'plain:', uniquePlainKeys.length, ')');
        if (localCount > 0) {
          totalCompleted = localCount;
          Object.values(localCompleted).forEach(workout => {
            if (workout.completedAt) {
              allDates.push(new Date(workout.completedAt).toDateString());
            }
          });
        }
      } catch (e) {
        console.log('📊 Dashboard: localStorage parse error:', e);
      }

      // Also check program completions in localStorage
      try {
        const programCompletions = JSON.parse(localStorage.getItem('programCompletions') || '{}');
        let programWorkoutCount = 0;
        Object.values(programCompletions).forEach(completions => {
          if (Array.isArray(completions)) programWorkoutCount += completions.length;
        });
        console.log('📊 Dashboard: localStorage programCompletions workout count:', programWorkoutCount);
        if (programWorkoutCount > totalCompleted) {
          totalCompleted = programWorkoutCount;
        }
      } catch (e) { /* ignore */ }

      console.log('📊 Dashboard: userEmail:', userEmail);

      if (userEmail) {
        try {
          // Get completed workouts from Supabase workout_history table
          const completedResult = await getCompletedWorkouts(userEmail);
          console.log('📊 Dashboard: Supabase result:', completedResult.success, 'totalCount:', completedResult.totalCount, 'keys:', Object.keys(completedResult.data || {}).length);

          if (completedResult.success) {
            // Supabase totalCount is the authoritative source (properly deduped)
            // localStorage count can be inflated due to double-key storage
            totalCompleted = completedResult.totalCount;
            
            // Collect dates for streak calculation
            allDates = [];
            Object.values(completedResult.data).forEach(workout => {
              if (workout.completedAt) {
                allDates.push(new Date(workout.completedAt).toDateString());
              }
            });
          }
        } catch (error) {
          console.error('📊 Dashboard: Supabase error:', error);
        }
      }

      console.log('📊 Dashboard: Final workoutsCompleted:', totalCompleted);
      setWorkoutsCompleted(totalCompleted);

      // Calculate streak (consecutive days with workouts in the last 30 days)
      const uniqueDates = [...new Set(allDates)].sort((a, b) => new Date(b) - new Date(a));
      let streak = 0;
      const today = new Date();
      for (let i = 0; i < 30; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(today.getDate() - i);
        if (uniqueDates.includes(checkDate.toDateString())) {
          streak++;
        } else if (i > 0) {
          break; // Streak broken
        }
      }
      setWorkoutStreak(streak);

      // Get user goals for progress calculation
      if (userEmail) {
        try {
          const goalsResult = await getUserGoals(userEmail);
          if (goalsResult.success && goalsResult.data) {
            const goalsData = goalsResult.data;

            // Back-compat: older format stored as an array of goals with a numeric progress field
            if (Array.isArray(goalsData)) {
              if (goalsData.length > 0) {
                const totalProgress = goalsData.reduce((sum, goal) => sum + (goal?.progress || 0), 0);
                const avgProgress = Math.round(totalProgress / goalsData.length);
                setGoalProgress(avgProgress);
              } else {
                setGoalProgress(0);
              }
            } else {
              // Current format: payload from MyGoals (goalsV2)
              const payload = goalsData || {};
              const weeklyGoals = Array.isArray(payload.weeklyGoals) ? payload.weeklyGoals : [];
              const habits = Array.isArray(payload.habits) ? payload.habits : [];
              const habitChecks = payload.habitChecks && typeof payload.habitChecks === 'object' ? payload.habitChecks : {};
              const detailedGoals = Array.isArray(payload.detailedGoals) ? payload.detailedGoals : [];

              const progressParts = [];

              // Detailed goals (steps-based)
              if (detailedGoals.length > 0) {
                const getDetailedGoalProgress = (goal) => {
                  if (Array.isArray(goal?.steps) && goal.steps.length > 0) {
                    const done = goal.steps.filter((s) => s.completed).length;
                    return Math.round((done / goal.steps.length) * 100);
                  }
                  return typeof goal?.progress === 'number' ? goal.progress : 0;
                };
                const avgDetailed = Math.round(
                  detailedGoals.reduce((sum, g) => sum + getDetailedGoalProgress(g), 0) / detailedGoals.length
                );
                progressParts.push(avgDetailed);
              }

              // Weekly focus goals
              if (weeklyGoals.length > 0) {
                const done = weeklyGoals.filter((g) => g?.done).length;
                progressParts.push(Math.round((done / weeklyGoals.length) * 100));
              }

              // Habits (this week's checkmarks)
              if (habits.length > 0) {
                const now = new Date();
                const day = now.getDay();
                const diffToMonday = (day + 6) % 7;
                const weekStart = new Date(now);
                weekStart.setHours(0, 0, 0, 0);
                weekStart.setDate(now.getDate() - diffToMonday);
                const fmt = (d) => d.toISOString().split('T')[0];
                const weekDates = Array.from({ length: 7 }, (_, i) => {
                  const d = new Date(weekStart);
                  d.setDate(weekStart.getDate() + i);
                  return fmt(d);
                });

                const totalPossible = habits.length * weekDates.length;
                let completed = 0;
                habits.forEach((h) => {
                  weekDates.forEach((dateStr) => {
                    const key = `${h.id}_${dateStr}`;
                    if (habitChecks[key]) completed += 1;
                  });
                });
                progressParts.push(Math.round((completed / totalPossible) * 100));
              }

              if (progressParts.length > 0) {
                const avg = Math.round(progressParts.reduce((s, p) => s + p, 0) / progressParts.length);
                setGoalProgress(avg);
              } else {
                setGoalProgress(0);
              }
            }
          }
        } catch (error) {
          console.error('Error loading goals:', error);
        }
      }

      setLoadingStats(false);
    };

    loadStats();

    // Load a featured resource for Wellness Hub
    const loadFeaturedResource = async () => {
      try {
        const { data: resources } = await supabase
          .from('resources')
          .select('id, title, description, type, category')
          .limit(10);
        
        if (resources && resources.length > 0) {
          // Pick a random resource or rotate daily
          const today = new Date();
          const dayIndex = today.getDate() % resources.length;
          setFeaturedResource(resources[dayIndex]);
        }
      } catch (error) {
        console.error('Error loading featured resource:', error);
      } finally {
        setLoadingResource(false);
      }
    };
    loadFeaturedResource();
    
    // Listen for workout completion events to refresh stats in real-time
    const handleWorkoutCompleted = () => {
      console.log('🔄 Workout completed - refreshing dashboard stats...');
      loadStats();
    };

    // Listen for goals updates from Goals & Reflection page
    const handleGoalsUpdated = () => {
      loadStats();
    };
    
    window.addEventListener('workoutCompleted', handleWorkoutCompleted);
    window.addEventListener('goalsUpdated', handleGoalsUpdated);
    
    return () => {
      window.removeEventListener('workoutCompleted', handleWorkoutCompleted);
      window.removeEventListener('goalsUpdated', handleGoalsUpdated);
    };
  }, []);

  // Weight tracking state - load from Supabase
  const [weightData, setWeightData] = useState([]);
  const [weightStarting, setWeightStarting] = useState('');
  const [weightGoalType, setWeightGoalType] = useState('lose');
  const [loadingWeight, setLoadingWeight] = useState(true);

  useEffect(() => {
    const checkWelcomeModal = async () => {
      try {
        const status = localStorage.getItem('memberStatus');
        const shouldShowOnFirstNav = localStorage.getItem('showWelcomeOnFirstNav') === 'true';
        const userEmail = localStorage.getItem('userEmail');
        
        // Also check localStorage backup — if user already dismissed it, don't show again
        const alreadySeenLocally = localStorage.getItem('welcomeModalSeen') === 'true';
        
        if (status === 'active' && shouldShowOnFirstNav && userEmail && !alreadySeenLocally) {
          // Check Supabase for welcome modal status
          const result = await getWelcomeModalStatus(userEmail);
          
          if (result.success && !result.hasSeen) {
            setShowWelcomeModal(true);
          }
          
          // Clear the flag so we don't check again on subsequent page loads
          localStorage.removeItem('showWelcomeOnFirstNav');
        }
      } catch (e) {
        // ignore read errors
      }
    };
    
    checkWelcomeModal();
  }, []);

  // Load weight data from Supabase
  useEffect(() => {
    const loadWeightData = async () => {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        setLoadingWeight(false);
        return;
      }

      try {
        // Load weight data from My Progress entries
        const entriesResult = await getProgressEntries(userEmail);
        if (entriesResult.success) {
          const weights = (entriesResult.data || [])
            .filter((e) => e && e.weight !== null && e.weight !== undefined && e.weight !== '')
            .map((e) => ({
              id: e.id,
              date: e.date,
              weight: typeof e.weight === 'number' ? e.weight : parseFloat(e.weight)
            }))
            .filter((e) => !Number.isNaN(e.weight));
          setWeightData(weights);
        }

        // Load weight settings (starting weight, goal type)
        const settingsResult = await getWeightSettings(userEmail);
        if (settingsResult.success && settingsResult.data) {
          setWeightStarting(settingsResult.data.starting_weight || '');
          setWeightGoalType(settingsResult.data.goal_type || 'lose');
        }
      } catch (error) {
        console.error('Error loading weight data:', error);
      } finally {
        setLoadingWeight(false);
      }
    };

    loadWeightData();

    // Listen for weight updates from My Goals page
    const handleWeightUpdate = () => {
      loadWeightData();
    };
    window.addEventListener('weightUpdated', handleWeightUpdate);
    
    return () => {
      window.removeEventListener('weightUpdated', handleWeightUpdate);
    };
  }, []);

  // Determine which suggestion to show today (relative to first visit)
  useEffect(() => {
    try {
      const key = 'focusStartDate';
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      let stored = localStorage.getItem(key);
      if (!stored) {
        localStorage.setItem(key, todayStart.toISOString());
        stored = todayStart.toISOString();
      }
      const start = new Date(stored);
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const diffDays = Math.floor((todayStart - startDay) / (1000 * 60 * 60 * 24));
      const idx = ((diffDays % focusSuggestions.length) + focusSuggestions.length) % focusSuggestions.length;
      setFocusIndex(idx);
    } catch (e) {
      // fallback index 0
      setFocusIndex(0);
    }
  }, []);

  // Avatar and name are now loaded from Supabase in loadUserProfile useEffect above
  // localStorage is only used as a cache for instant display on page load

  // Listen for avatar/name updates (from profile page or other tabs)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'memberAvatarUrl') {
        setAvatarUrl(e.newValue || null);
      }
      if (e.key === 'memberProfileName') {
        setProfileName(e.newValue || '');
      }
    };
    const onAvatarUpdated = (e) => {
      const next = (e && e.detail && e.detail.url) || localStorage.getItem('memberAvatarUrl') || null;
      setAvatarUrl(next);
    };
    const onNameUpdated = (e) => {
      const name = (e && e.detail && e.detail.name) || localStorage.getItem('memberProfileName') || '';
      setProfileName(name || '');
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('memberAvatarUpdated', onAvatarUpdated);
    window.addEventListener('memberProfileNameUpdated', onNameUpdated);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('memberAvatarUpdated', onAvatarUpdated);
      window.removeEventListener('memberProfileNameUpdated', onNameUpdated);
    };
  }, []);

  const closeWelcomeModal = async () => {
    const userEmail = localStorage.getItem('userEmail');
    
    if (userEmail) {
      // Save to Supabase for cross-device sync
      await markWelcomeModalAsSeen(userEmail);
    }
    
    // Also save to localStorage as backup
    try { localStorage.setItem('welcomeModalSeen', 'true'); } catch (e) {}
    setShowWelcomeModal(false);
  };



  const dashboardSections = [
    // Large featured cards with images
    {
      id: 'recipes',
      title: 'Recipe Library',
      icon: '🍽️',
      description: 'Browse allergy-friendly recipes with advanced filtering',
      items: ['New recipes added monthly', 'Filter by Allergies', 'Save Favorites', 'Nutrition Info'],
      route: '/recipes',
      color: 'from-sage-50 to-sage-100',
      iconBg: 'bg-sage-500',
      image: true,
      imageUrl: '/images/Low-Carb-Greek-Chicken-Bowls-square.jpg'
    },
    {
      id: 'workouts',
      title: 'Workout Library',
      icon: '💪',
      description: 'Monthly programs and exercise tutorials',
      items: ['Monthly Programs', 'Exercise Videos', 'Form Guides', 'Progress Tracking'],
      route: '/workouts',
      color: 'from-blue-100 to-cyan-100',
      iconBg: 'bg-blue-500',
      image: true,
      imageUrl: '/images/workout-hero-optimized.jpg'
    },
    // Compact cards - ordered as: Wellness Hub, Cycle Tracker, Goals
    {
      id: 'resources',
      title: 'Wellness Hub',
      icon: '/images/wellnesshub.png',
      isImageIcon: true,
      description: 'Guides, e-books and tools',
      items: ['Pantry Guide', 'Video Library', 'E-books', 'Meal Planners'],
      route: '/resources',
      color: 'from-teal-100 to-cyan-100',
      iconBg: 'bg-teal-500'
    },
    {
      id: 'cycle-tracker',
      title: 'Cycle Tracker',
      icon: '/images/cycletrackingpage.png',
      isImageIcon: true,
      description: 'Track your cycle and understand hormone-skin connections',
      items: ['Period Logging', 'Symptom Tracking', 'Skin Insights', 'Cycle Predictions'],
      route: '/cycle-tracker',
      color: 'from-rose-100 to-pink-100',
      iconBg: 'bg-rose-500'
    },
    {
      id: 'goals',
      title: 'Goals & Reflection',
      icon: '/images/Journalpage.png',
      isImageIcon: true,
      description: 'Set goals, track progress, and journal your journey',
      items: ['Goal Setting', 'Weekly Focus', 'Reflection Prompts', 'Progress Tracking'],
      route: '/my-goals',
      color: 'from-orange-100 to-amber-100',
      iconBg: 'bg-orange-500'
    },
    {
      id: 'progress',
      title: 'My Progress',
      icon: '📸',
      isImageIcon: false,
      image: true,
      description: 'Track your transformation with photos and weight',
      items: ['Progress Photos', 'Weight Tracking', 'Before & After', 'Compare Mode'],
      route: '/my-progress',
      color: 'from-purple-100 to-violet-100',
      iconBg: 'bg-purple-500'
    },
    // Sidebar feature
    {
      id: 'forum',
      title: 'Community Forum',
      icon: '💬',
      description: 'Connect with members and share your journey',
      items: ['Discussion Groups', 'Success Stories', "Women's Health"],
      route: '/forum',
      color: 'from-pink-100 to-rose-100',
      iconBg: 'bg-pink-500'
    }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-sage-50 via-beige-50 to-rose-50 pt-20 px-3 sm:px-6">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[32rem] h-[32rem] rounded-full blur-3xl" style={{ backgroundColor: 'rgba(168, 181, 160, 0.2)' }}></div>
        <div className="absolute top-1/3 -right-20 w-[36rem] h-[36rem] rounded-full blur-3xl" style={{ backgroundColor: 'rgba(217, 167, 156, 0.15)' }}></div>
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] rounded-full blur-3xl" style={{ backgroundColor: 'rgba(232, 235, 231, 0.3)' }}></div>
        <div className="absolute bottom-[-6rem] left-1/3 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'rgba(168, 181, 160, 0.25)' }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 md:mb-8"
        >
          <div className="flex items-center justify-between gap-3">
            {/* Left: Welcome Message */}
            <div className="flex-1 min-w-0">
              <div className="text-sm md:text-base lg:text-lg text-charcoal-600 font-medium mb-1">
                {(() => {
                  const hour = new Date().getHours();
                  if (hour < 12) return '☀️ Good morning';
                  if (hour < 18) return '🌤️ Good afternoon';
                  return '🌙 Good evening';
                })()}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-playfair font-semibold text-charcoal-900 truncate">
                {(() => {
                  const firstName = (profileName || '').trim().split(/\s+/)[0] || 'Member';
                  return firstName;
                })()}
              </h1>
            </div>

            {/* Right: Notifications & Profile Picture */}
            <div className="flex items-center gap-3 flex-shrink-0">
                {/* Notification Bell */}
                <NotificationBell />
                
                {/* Profile Picture */}
                <RouterLink 
                  to="/profile" 
                  className="relative group" 
                  aria-label="Open your profile"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="Your profile"
                      className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full object-cover ring-2 ring-sage-300 group-hover:ring-sage-400 transition-all cursor-pointer hover:scale-105"
                    />
                  ) : (
                    <div className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full ring-2 ring-sage-300 group-hover:ring-sage-400 transition-all bg-gradient-to-br from-sage-200 to-sage-300 text-sage-800 flex items-center justify-center font-bold text-base md:text-lg lg:text-xl shadow-inner cursor-pointer hover:scale-105">
                      {(() => {
                        const parts = (profileName || '').trim().split(/\s+/);
                        const a = parts[0]?.[0] || '';
                        const b = parts[1]?.[0] || '';
                        const init = (a + b).toUpperCase() || (profileName?.[0]?.toUpperCase() || 'A');
                        return init;
                      })()}
                    </div>
                  )}
                </RouterLink>
              </div>
            </div>
        </motion.div>
        
        {/* Push Notification Prompt */}
        <PushNotificationPrompt />
        
        {/* One-time Welcome Modal */}
        {/* PWA Install Banner */}
        {showPwaPrompt && !showWelcomeModal && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mx-3 sm:mx-0 mb-4 rounded-2xl overflow-hidden shadow-soft border border-sage-200/60"
            style={{ background: 'linear-gradient(135deg, #f5f0e8 0%, #e8ede4 50%, #f0ece4 100%)' }}
          >
            <div className="p-5 relative">
              <button onClick={dismissPwaPrompt} className="absolute top-4 right-4 text-charcoal-300 hover:text-charcoal-500 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <p className="text-charcoal-800 text-sm font-playfair font-semibold mb-1.5">Get the full experience</p>
              <p className="text-charcoal-500 text-xs leading-relaxed mb-3">
                Add Alera to your home screen for a seamless experience — workout notifications, full-screen mode, and quick access whenever you need it.
              </p>
              <div className="bg-white/50 backdrop-blur-sm rounded-xl p-3 text-xs text-charcoal-600 leading-relaxed" style={{ borderLeft: '3px solid #a8b5a0' }}>
                {isIOS ? (
                  <p>In Safari, tap the <strong>Share button</strong> (box with arrow) at the bottom of your screen, then scroll down and tap <strong>"Add to Home Screen"</strong></p>
                ) : (
                  <p>In Chrome, tap the <strong>menu</strong> (three dots) in the top right, then tap <strong>"Add to Home Screen"</strong> or <strong>"Install app"</strong></p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {showWelcomeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={closeWelcomeModal}></div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="relative bg-white rounded-3xl shadow-soft p-6 max-w-2xl w-full border border-sage-200"
            >
              <div className="flex items-start justify-between mb-3">
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900">Welcome to Alera, {profileName.split(' ')[0] || 'Member'}! 🌱</h2>
                <button onClick={closeWelcomeModal} className="text-charcoal-400 hover:text-charcoal-600">✕</button>
              </div>
              <p className="text-charcoal-600 text-sm leading-relaxed mb-2">
                Thank you for being part of our wellness community. Your commitment to healing inspires us, and we're honored to support your journey.
              </p>
              <p className="text-charcoal-500 text-sm leading-relaxed">
                This is your personal wellness command center with everything you need: allergy-friendly recipes, gentle workout programs, supportive community, goal tracking, and curated resources. Every tool is crafted with love to help you thrive - explore at your own pace and remember, you're exactly where you need to be. ✨
              </p>
              <div className="mt-4 text-right">
                <button onClick={closeWelcomeModal} className="bg-sage-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-sage-700 transition-colors">Let's go</button>
              </div>
            </motion.div>
          </div>
        )}

        

        {showSupportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowSupportModal(false)}></div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="relative bg-white rounded-3xl shadow-soft p-5 max-w-sm w-full border border-sage-200"
            >
              <div className="flex items-start justify-between mb-2">
                <h2 className="text-lg font-semibold text-charcoal-900">Need Help?</h2>
                <button onClick={() => setShowSupportModal(false)} className="text-charcoal-400 hover:text-charcoal-600">✕</button>
              </div>
              <p className="text-sm text-charcoal-600 mb-4">We’re here for you. Choose an option below:</p>
              <div className="space-y-2">
                <RouterLink
                  to="/forum?tab=qna"
                  className="block text-center bg-sage-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-sage-700 transition-colors"
                  onClick={() => setShowSupportModal(false)}
                >
                  Ask the Community
                </RouterLink>
                <a
                  href="mailto:hello@alera.co.nz"
                  className="block text-center bg-sage-100 text-sage-800 px-4 py-2 rounded-xl text-sm font-medium hover:bg-sage-200 transition-colors"
                  onClick={() => setShowSupportModal(false)}
                >
                  Email Support
                </a>
              </div>
            </motion.div>
          </div>
        )}

        

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-8">
          {/* Left Stats Column (horizontal scroll on mobile, stacked on desktop) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="order-3 lg:order-1"
          >
            {/* Mobile: Horizontal Scroll Grid */}
            <div className="lg:hidden mb-6">
              <h3 className="text-lg font-semibold text-charcoal-900 mb-3 px-1">Your Progress</h3>
              <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                {/* Workouts Completed */}
                <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-soft min-w-[160px] snap-start">
                  <div className="flex items-center mb-1">
                    <span className="text-xl mr-2">💪</span>
                    <div className="text-xl font-bold text-blue-600">{loadingStats ? '...' : workoutsCompleted}</div>
                  </div>
                  <div className="text-xs text-charcoal-600 font-medium">Workouts</div>
                  <div className="text-xs text-blue-600 mt-1">{workoutsCompleted > 0 ? 'Great progress!' : 'Start your first workout!'}</div>
                </div>

                {/* Goal Progress */}
                <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-soft min-w-[160px] snap-start">
                  <div className="flex items-center mb-1">
                    <span className="text-xl mr-2">🎯</span>
                    <div className="text-xl font-bold text-purple-600">{loadingStats ? '...' : `${goalProgress}%`}</div>
                  </div>
                  <div className="text-xs text-charcoal-600 font-medium">Goal Progress</div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                    <div className="bg-purple-600 h-1.5 rounded-full" style={{width: `${goalProgress}%`}}></div>
                  </div>
                </div>

                {/* Workout Streak */}
                <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-soft min-w-[160px] snap-start">
                  <div className="flex items-center mb-1">
                    <span className="text-xl mr-2">🔥</span>
                    <div className="text-xl font-bold text-orange-600">{loadingStats ? '...' : workoutStreak}</div>
                  </div>
                  <div className="text-xs text-charcoal-600 font-medium">Day Streak</div>
                  <div className="text-xs mt-1" style={{ color: '#a8b5a0' }}>{workoutStreak > 0 ? 'Keep it up!' : 'Start your streak!'}</div>
                </div>
              </div>
            </div>

            <div className="lg:hidden bg-white/80 backdrop-blur-sm p-4 rounded-2xl shadow-soft mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-charcoal-900 flex items-center">
                  <span className="text-lg mr-2">⭐</span>
                  Your Favorites
                </h3>
                <RouterLink 
                  to="/my-alera" 
                  className="text-xs text-sage-600 hover:text-sage-700 font-medium"
                >
                  View All →
                </RouterLink>
              </div>
              <div className="relative">
                {loadingFavorites ? (
                  <div className="p-6 text-center">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto"></div>
                    </div>
                  </div>
                ) : favoriteItems.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <button
                        aria-label="Previous favorite"
                        className="px-2 py-1 rounded-lg bg-sage-100 text-sage-800 text-xs hover:bg-sage-200"
                        onClick={() => setFavIndex((favIndex - 1 + favoriteItems.length) % favoriteItems.length)}
                      >
                        ←
                      </button>
                      <div className="text-[11px] text-charcoal-500">{favIndex + 1} / {favoriteItems.length}</div>
                      <button
                        aria-label="Next favorite"
                        className="px-2 py-1 rounded-lg bg-sage-100 text-sage-800 text-xs hover:bg-sage-200"
                        onClick={() => setFavIndex((favIndex + 1) % favoriteItems.length)}
                      >
                        →
                      </button>
                    </div>

                    <RouterLink to={favoriteItems[favIndex].to} className={`block p-3 rounded-xl ${favoriteItems[favIndex].color} transition`}> 
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-charcoal-800 truncate">{favoriteItems[favIndex].title}</div>
                          <div className="text-xs text-charcoal-600">{favoriteItems[favIndex].subtitle}</div>
                        </div>
                        <span className="ml-3 text-lg">{favoriteItems[favIndex].icon}</span>
                      </div>
                    </RouterLink>

                    <div className="mt-2 flex gap-1 justify-center">
                      {favoriteItems.map((_, i) => (
                        <button
                          key={i}
                          aria-label={`Go to item ${i + 1}`}
                          onClick={() => setFavIndex(i)}
                          className={`w-1.5 h-1.5 rounded-full ${i === favIndex ? 'bg-sage-600' : 'bg-sage-300'} hover:bg-sage-500`}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-sm text-charcoal-500">No favorites yet</p>
                    <p className="text-xs text-charcoal-400 mt-1">Save recipes and articles to see them here</p>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop: Stacked Layout */}
            <div className="hidden lg:block space-y-4">
              {/* Workouts Completed */}
              <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-soft">
                <div className="flex items-center mb-0.5">
                  <span className="text-base mr-2">💪</span>
                  <div className="text-base font-bold text-blue-600">{loadingStats ? '...' : workoutsCompleted}</div>
                </div>
                <div className="text-xs text-charcoal-600">Workouts Completed</div>
                <div className="text-xs text-blue-600 mt-0.5">{workoutsCompleted > 0 ? 'Great progress this month!' : 'Start your first workout!'}</div>
              </div>

              {/* Goal Progress */}
              <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-soft">
                <div className="flex items-center mb-0.5">
                  <span className="text-base mr-2">🎯</span>
                  <div className="text-base font-bold text-purple-600">{loadingStats ? '...' : `${goalProgress}%`}</div>
                </div>
                <div className="text-xs text-charcoal-600">Goal Progress</div>
                <div className="w-full bg-gray-200 rounded-full h-1 mt-0.5">
                  <div className="bg-purple-600 h-1 rounded-full" style={{width: `${goalProgress}%`}}></div>
                </div>
              </div>

              {/* Workout Streak */}
              <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-soft">
                <div className="flex items-center mb-0.5">
                  <span className="text-base mr-2">🔥</span>
                  <div className="text-base font-bold text-orange-600">{loadingStats ? '...' : workoutStreak}</div>
                </div>
                <div className="text-xs text-charcoal-600">Workout Streak</div>
                <div className="text-xs mt-0.5" style={{ color: '#a8b5a0' }}>{workoutStreak > 0 ? `${workoutStreak} day streak - keep it up!` : 'Start your streak today!'}</div>
              </div>

              {/* Weight & Progress Photos */}
            <RouterLink to="/my-progress" className="block bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-soft hover:shadow-md transition-shadow">
              {loadingWeight ? (
                <>
                  <div className="flex items-center mb-1">
                    <span className="text-base mr-2">⚖️</span>
                    <div className="text-base font-bold text-charcoal-600">Loading...</div>
                  </div>
                  <div className="text-xs text-charcoal-600 mb-2">Weight Progress</div>
                  <div className="h-8"></div>
                </>
              ) : weightData.length > 0 ? (
                <>
                  {(() => {
                    const sortedByDateDesc = [...weightData].sort((a, b) => new Date(b.date) - new Date(a.date));
                    const sortedByDateAsc = [...weightData].sort((a, b) => new Date(a.date) - new Date(b.date));

                    const currentWeight = sortedByDateDesc[0]?.weight;
                    const startWeight = weightStarting ? parseFloat(weightStarting) : null;
                    const hasStart = startWeight !== null && !Number.isNaN(startWeight);
                    const change = hasStart ? currentWeight - startWeight : null;
                    const isGaining = weightGoalType === 'gain';
                    const isLosing = weightGoalType === 'lose';
                    const onTrack = change === null ? true : (isGaining && change > 0) || (isLosing && change < 0) || change === 0;

                    const weightsOnly = sortedByDateAsc.map((e) => e.weight);
                    const minW = Math.min(...weightsOnly);
                    const maxW = Math.max(...weightsOnly);
                    const range = maxW - minW || 1;
                    const points = sortedByDateAsc
                      .map((e, i) => {
                        const x = sortedByDateAsc.length === 1 ? 50 : (i / (sortedByDateAsc.length - 1)) * 100;
                        const y = 22 - ((e.weight - minW) / range) * 20;
                        return `${x},${y}`;
                      })
                      .join(' ');
                    
                    return (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center">
                            <span className="text-base mr-2">⚖️</span>
                            <div className="text-base font-bold text-charcoal-800">{typeof currentWeight === 'number' ? currentWeight.toFixed(1) : ''} kg</div>
                          </div>
                          {change !== null && change !== 0 && (
                            <div className={`text-xs font-semibold ${onTrack ? 'text-green-600' : 'text-orange-600'}`}>
                              {change > 0 ? '+' : ''}{change.toFixed(1)} kg
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-charcoal-600 mb-2">Weight Progress</div>

                        <svg className="w-full h-8" viewBox="0 0 100 24" preserveAspectRatio="none">
                          <polyline fill="none" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
                        </svg>

                        <div className="flex items-center justify-between">
                          <div className="text-xs text-charcoal-500">
                            {weightData.length} {weightData.length === 1 ? 'entry' : 'entries'}
                          </div>
                          {hasStart && change !== 0 && (
                            <div className="text-xs text-charcoal-500">
                              From {startWeight} kg
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </>
              ) : weightStarting ? (
                <>
                  <div className="flex items-center mb-1">
                    <span className="text-base mr-2">⚖️</span>
                    <div className="text-base font-bold text-teal-600">{weightStarting} kg</div>
                  </div>
                  <div className="text-xs text-charcoal-600 mb-2">Starting Weight</div>
                  <div className="text-xs text-charcoal-500 text-center py-3">Log your first weigh-in</div>
                </>
              ) : (
                <>
                  <div className="flex items-center mb-1">
                    <span className="text-base mr-2">⚖️</span>
                    <div className="text-base font-bold text-charcoal-600">Track Weight</div>
                  </div>
                  <div className="text-xs text-charcoal-600 mb-2">Weight Progress</div>
                  <div className="text-xs text-charcoal-500 text-center py-3">Click to start tracking</div>
                </>
              )}
            </RouterLink>

            {/* Your Favorites (mini carousel) */}
            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl shadow-soft">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-charcoal-900 flex items-center">
                  <span className="text-lg mr-2">⭐</span>
                  Your Favorites
                </h3>
                <RouterLink 
                  to="/my-alera" 
                  className="text-xs text-sage-600 hover:text-sage-700 font-medium"
                >
                  View All →
                </RouterLink>
              </div>
              <div className="relative">
                {loadingFavorites ? (
                  <div className="p-6 text-center">
                    <div className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2 mx-auto"></div>
                    </div>
                  </div>
                ) : favoriteItems.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <button
                        aria-label="Previous favorite"
                        className="px-2 py-1 rounded-lg bg-sage-100 text-sage-800 text-xs hover:bg-sage-200"
                        onClick={() => setFavIndex((favIndex - 1 + favoriteItems.length) % favoriteItems.length)}
                      >
                        ←
                      </button>
                      <div className="text-[11px] text-charcoal-500">{favIndex + 1} / {favoriteItems.length}</div>
                      <button
                        aria-label="Next favorite"
                        className="px-2 py-1 rounded-lg bg-sage-100 text-sage-800 text-xs hover:bg-sage-200"
                        onClick={() => setFavIndex((favIndex + 1) % favoriteItems.length)}
                      >
                        →
                      </button>
                    </div>

                    <RouterLink to={favoriteItems[favIndex].to} className={`block p-3 rounded-xl ${favoriteItems[favIndex].color} transition`}> 
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-charcoal-800 truncate">{favoriteItems[favIndex].title}</div>
                          <div className="text-xs text-charcoal-600">{favoriteItems[favIndex].subtitle}</div>
                        </div>
                        <span className="ml-3 text-lg">{favoriteItems[favIndex].icon}</span>
                      </div>
                    </RouterLink>

                    <div className="mt-2 flex gap-1 justify-center">
                      {favoriteItems.map((_, i) => (
                        <button
                          key={i}
                          aria-label={`Go to item ${i + 1}`}
                          onClick={() => setFavIndex(i)}
                          className={`w-1.5 h-1.5 rounded-full ${i === favIndex ? 'bg-sage-600' : 'bg-sage-300'} hover:bg-sage-500`}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-sm text-charcoal-500">No favorites yet</p>
                    <p className="text-xs text-charcoal-400 mt-1">Save recipes and articles to see them here</p>
                  </div>
                )}
              </div>
            </div>

              {/* Support - Desktop Only */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="bg-gradient-to-br from-sage-100 to-beige-100 p-6 rounded-3xl shadow-soft flex flex-col hidden lg:block"
              >
                <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Need Help?</h3>
                <p className="text-sm text-charcoal-600 mb-3">Support from Kate & community</p>
                <button 
                  onClick={() => setShowSupportModal(true)}
                  className="w-full bg-sage-600 text-white py-2 px-4 rounded-2xl text-xs font-medium hover:bg-sage-700 transition-colors duration-200"
                >
                  Contact support
                </button>
              </motion.div>
            </div>
          </motion.div>

          {/* Main Content */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            {/* Large Featured Cards - Recipes & Workouts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-4 mb-6"
            >
              {dashboardSections.filter(s => s.imageUrl).map((section, index) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  whileHover={{ y: -5, scale: 1.01 }}
                  className={`bg-gradient-to-br ${section.color} p-5 md:p-8 rounded-2xl md:rounded-3xl shadow-soft hover:shadow-soft-lg transition-all duration-300 cursor-pointer border border-white/50`}
                  role="button"
                  tabIndex={0}
                  onClick={() => section.route && navigate(section.route)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      section.route && navigate(section.route);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 md:gap-6">
                    <div className="w-20 h-20 md:w-28 md:h-28 overflow-hidden ring-2 ring-white/60 bg-white/40 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0">
                      <img src={section.imageUrl} alt={`${section.title} preview`} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <h3 className="text-base md:text-xl font-semibold text-charcoal-900 flex-1 truncate">{section.title}</h3>
                      </div>
                      <p className="text-charcoal-700 mt-1 text-xs md:text-sm leading-snug"
                         style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {section.description}
                      </p>
                      {Array.isArray(section.items) && section.items.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5 md:gap-2">
                          {section.items.slice(0, 2).map((chip, idx) => (
                            <span key={idx} className="px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[10px] md:text-[11px] bg-white/70 border border-white/60 text-charcoal-700">
                              {chip}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Smaller Cards - Cycle Tracker, Goals, Wellness Hub */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-4"
            >
              {dashboardSections.filter(s => (s.image || s.isImageIcon) && !s.imageUrl && s.id !== 'forum' && s.id !== 'resources').map((section, index) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  whileHover={{ y: -3, scale: 1.01 }}
                  className={`bg-gradient-to-br ${section.color} p-4 md:p-5 rounded-xl md:rounded-2xl shadow-soft hover:shadow-soft-lg transition-all duration-300 cursor-pointer border border-white/50 min-h-[100px]`}
                  role="button"
                  tabIndex={0}
                  onClick={() => section.route && navigate(section.route)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      section.route && navigate(section.route);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg md:rounded-xl bg-white/40 flex items-center justify-center flex-shrink-0 ring-2 ring-white/60 overflow-hidden">
                      {section.isImageIcon ? (
                        <img src={section.icon} alt={section.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <span className="text-3xl">{section.icon}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {section.badge && (
                        <div className="text-[10px] uppercase tracking-wide text-rose-600/80 font-semibold mb-0.5">
                          {section.badge}
                        </div>
                      )}
                      <h3 className="text-sm md:text-base font-semibold text-charcoal-900 mb-0.5 md:mb-1">{section.title}</h3>
                      <p className="text-charcoal-700 text-[11px] md:text-xs leading-snug line-clamp-1">
                        {section.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 order-2 lg:order-3">
            {/* Today's Suggested Action */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gradient-to-br from-orange-50 to-yellow-50 p-4 md:p-5 rounded-2xl md:rounded-3xl shadow-soft border border-orange-100 flex flex-col"
            >
              <h3 className="text-base md:text-lg font-semibold text-charcoal-900 mb-2 flex items-center">
                <span className="text-xl md:text-2xl mr-2">✨</span>
                Today's Focus
              </h3>
              <div className="bg-white/70 p-3 rounded-2xl">
                <div className="text-base font-medium text-charcoal-800 mb-1 leading-snug">
                  {focusSuggestions[focusIndex]?.title || 'Today\'s focus'}
                </div>
                <div className="text-xs text-charcoal-600 mb-2 leading-snug">
                  {focusSuggestions[focusIndex]?.subtitle || ''}
                </div>
                <RouterLink 
                  to={focusSuggestions[focusIndex]?.to || '/'} 
                  className="inline-block bg-orange-500 text-white px-3 py-1.5 rounded-xl text-xs font-medium hover:bg-orange-600 transition-colors"
                >
                  {focusSuggestions[focusIndex]?.cta || 'Open'}
                </RouterLink>
              </div>
            </motion.div>

            {/* Community Forum */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className={`bg-gradient-to-br ${(dashboardSections.find(s => s.id === 'forum')?.color) || 'from-pink-100 to-rose-100'} p-6 rounded-3xl shadow-soft hover:shadow-soft-lg border border-white/50 h-56 sm:h-60 lg:h-64 xl:h-72 flex flex-col transition-all duration-300 cursor-pointer`}
              onClick={() => navigate('/forum')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/forum');
                }
              }}
            >
              <h3 className="text-xl font-semibold text-charcoal-900 mb-2 flex items-center">
                <span className="text-xl mr-2">👥</span>
                Community Forum
              </h3>
              <p className="text-sm text-charcoal-600 mb-3">Ask questions, share wins, and join supportive discussions.</p>
              <div className="grid grid-cols-2 gap-2 flex-1">
                <RouterLink 
                  to="/forum?tab=womens-health" 
                  className="p-3 bg-purple-50 rounded-xl text-sm text-purple-800 hover:bg-purple-100 transition-all hover:shadow-md hover:scale-105 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  Women's Health
                </RouterLink>
                <RouterLink 
                  to="/forum?tab=success-stories" 
                  className="p-3 bg-green-50 rounded-xl text-sm text-green-800 hover:bg-green-100 transition-all hover:shadow-md hover:scale-105 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  Success Stories
                </RouterLink>
                <RouterLink 
                  to="/forum?tab=qna" 
                  className="p-3 bg-blue-50 rounded-xl text-sm text-blue-800 hover:bg-blue-100 transition-all hover:shadow-md hover:scale-105 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  Q&A Threads
                </RouterLink>
                <RouterLink 
                  to="/forum?tab=challenges" 
                  className="p-3 bg-orange-50 rounded-xl text-sm text-orange-800 hover:bg-orange-100 transition-all hover:shadow-md hover:scale-105 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  Challenges
                </RouterLink>
              </div>
            </motion.div>

            {/* Wellness Hub - Community Resource */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              whileHover={{ y: -3, scale: 1.01 }}
              className="bg-gradient-to-br from-teal-100 to-cyan-100 p-4 rounded-2xl shadow-soft hover:shadow-soft-lg border border-white/50 flex flex-col transition-all duration-300 cursor-pointer min-h-[220px]"
              onClick={() => navigate('/resources')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/resources');
                }
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-semibold text-charcoal-900 flex items-center">
                  <span className="text-lg mr-2">📚</span>
                  Wellness Hub
                </h3>
                <span className="text-xs text-teal-600">View all →</span>
              </div>
              
              {/* Featured Resource Preview */}
              {loadingResource ? (
                <div className="bg-white/60 rounded-xl p-5 flex-1 animate-pulse">
                  <div className="h-5 bg-teal-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-teal-100 rounded w-full mb-2"></div>
                  <div className="h-4 bg-teal-100 rounded w-2/3 mb-2"></div>
                  <div className="h-3 bg-teal-100 rounded w-1/2"></div>
                </div>
              ) : featuredResource ? (
                <RouterLink
                  to={`/resources/${featuredResource.id}`}
                  className="bg-white/60 hover:bg-white/80 rounded-xl p-5 flex-1 transition-all group flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex-1">
                    <div className="text-xs text-teal-600 font-medium uppercase tracking-wide mb-1">
                      {featuredResource.category || 'Featured'}
                    </div>
                    <h4 className="text-base font-semibold text-charcoal-900 line-clamp-2 group-hover:text-teal-700 transition-colors mb-2">
                      {featuredResource.title}
                    </h4>
                    {featuredResource.description && (
                      <p className="text-xs text-charcoal-600 line-clamp-2">{featuredResource.description}</p>
                    )}
                  </div>
                  <div className="mt-auto pt-3 text-sm text-teal-600 font-medium group-hover:text-teal-700">Read more →</div>
                </RouterLink>
              ) : (
                <div className="bg-white/60 rounded-xl p-5 flex-1 flex items-center justify-center">
                  <p className="text-sm text-charcoal-500">Explore resources</p>
                </div>
              )}
            </motion.div>

            {/* Need Help - Mobile/Tablet Only */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-gradient-to-br from-sage-100 to-beige-100 p-6 rounded-3xl shadow-soft mt-6 lg:hidden"
            >
              <h3 className="text-lg font-semibold text-charcoal-900 mb-2">Need Help?</h3>
              <p className="text-sm text-charcoal-600 mb-3">Support from Kate & community</p>
              <button 
                onClick={() => setShowSupportModal(true)}
                className="w-full bg-sage-600 text-white py-2 px-4 rounded-2xl text-xs font-medium hover:bg-sage-700 transition-colors duration-200"
              >
                Contact support
              </button>
            </motion.div>
          </div>
        </div>

        {/* Back to Home */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-8"
        >
          <RouterLink
            to="/"
            className="text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors duration-200"
          >
            ← Back to Home
          </RouterLink>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
